import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, Space, Tag, Modal, Form, Input, DatePicker, InputNumber, message, Popconfirm, Tooltip, Drawer, Descriptions, Row, Col, Statistic, Switch, Divider, Typography, Checkbox, Collapse } from 'antd';
import dayjs from 'dayjs';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ReloadOutlined,
  SearchOutlined,
  GiftOutlined,
  ClockCircleOutlined,
  UserOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import { giftAPI, configAPI } from '../services/api';

const { TextArea } = Input;
const { Text } = Typography;
const { Panel } = Collapse;
const MAX_REWARD_COUNT = 2147483647;

function GiftManagement() {
  const [loading, setLoading] = useState(false);
  const [gifts, setGifts] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [searchKeyword, setSearchKeyword] = useState('');
  const [createVisible, setCreateVisible] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [logsVisible, setLogsVisible] = useState(false);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [currentGift, setCurrentGift] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPagination, setLogsPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const [itemList, setItemList] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemModalVisible, setItemModalVisible] = useState(false);
  const [itemSearchText, setItemSearchText] = useState('');
  const [levelModalVisible, setLevelModalVisible] = useState(false);
  const [currentItemForLevel, setCurrentItemForLevel] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);

  const [enabledConditions, setEnabledConditions] = useState([]);
  const { current: pageCurrent, pageSize } = pagination;

  const hasValue = (value) => value !== undefined && value !== null;

  const loadItemList = useCallback(async () => {
    try {
      const response = await configAPI.getItems();
      if (response.data.success) {
        const items = response.data.data || [];
        const formattedItems = items.map(item => ({
          id: item.item_id || item.id,
          name: item.name || `道具${item.item_id || item.id}`,
          type: item.type || 'resource',
          levels: item.levels || [],
          hasMultipleLevels: item.hasMultipleLevels || false,
          defaultLevel: item.defaultLevel || null
        }));
        setItemList(formattedItems);
      }
    } catch (error) {
      console.error('Load item list error:', error);
    }
  }, []);

  const handleSelectItem = (item) => {
    if (item.levels && item.levels.length > 0) {
      setCurrentItemForLevel(item);
      setSelectedLevel(item.levels[0].level);
      setLevelModalVisible(true);
      return;
    }

    const level = item.defaultLevel ?? 0;
    const uniqueId = `${item.id}_${level}`;
    const exists = selectedItems.find(i => i.uniqueId === uniqueId);
    if (!exists) {
      setSelectedItems([...selectedItems, { ...item, level, count: 1, uniqueId }]);
    } else {
      message.info('该道具已添加到列表中');
    }
  };

  const handleConfirmLevel = () => {
    if (!currentItemForLevel || !selectedLevel) return;

    const uniqueId = `${currentItemForLevel.id}_${selectedLevel}`;
    const exists = selectedItems.find(i => i.uniqueId === uniqueId);
    if (!exists) {
      setSelectedItems([...selectedItems, { ...currentItemForLevel, level: selectedLevel, count: 1, uniqueId }]);
    } else {
      message.info('该道具已添加到列表中');
    }
    setLevelModalVisible(false);
    setCurrentItemForLevel(null);
    setSelectedLevel(null);
  };

  const handleUpdateItemCount = (uniqueId, count) => {
    setSelectedItems(selectedItems.map(i => 
      (i.uniqueId || i.id) === uniqueId ? { ...i, count: parseInt(count) || 1 } : i
    ));
  };

  const handleRemoveItem = (uniqueId) => {
    setSelectedItems(selectedItems.filter(i => (i.uniqueId || i.id) !== uniqueId));
  };

  const handleOpenItemSelector = () => {
    setItemModalVisible(true);
  };

  const handleConfirmItems = () => {
    setItemModalVisible(false);
    message.success(`已选择 ${selectedItems.length} 个道具`);
  };

  const loadGifts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: pageCurrent, limit: pageSize };
      if (searchKeyword) params.keyword = searchKeyword;
      const response = await giftAPI.getList(params);
      setGifts(response.data.data.packages);
      setPagination(prev => ({ ...prev, total: response.data.data.pagination.total }));
    } catch (error) {
      message.error('加载福利列表失败');
    } finally {
      setLoading(false);
    }
  }, [pageCurrent, pageSize, searchKeyword]);

  useEffect(() => {
    loadGifts();
    loadItemList();
  }, [loadGifts, loadItemList]);

  const buildLimitConditions = (values) => {
    const conditions = [];

    if (enabledConditions.includes('account') && values.accountIds) {
      const ids = values.accountIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
      if (ids.length > 0) {
        conditions.push({ type: 'account', logic: 'AND', config: { account_ids: ids } });
      }
    }

    if (enabledConditions.includes('character') && values.characterIds) {
      const ids = values.characterIds.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
      if (ids.length > 0) {
        conditions.push({ type: 'character', logic: 'AND', config: { character_ids: ids } });
      }
    }

    if (enabledConditions.includes('purchase_limit') && hasValue(values.maxPurchaseCount)) {
      conditions.push({ type: 'purchase_limit', logic: 'AND', config: { max_purchase_count: values.maxPurchaseCount } });
    }

    if (enabledConditions.includes('daily_claim_limit') && hasValue(values.maxDailyClaimCount)) {
      conditions.push({ type: 'daily_claim_limit', logic: 'AND', config: { max_daily_claim_count: values.maxDailyClaimCount } });
    }

    if (enabledConditions.includes('register_time')) {
      const startDate = values.registerStartDate?.format('YYYY-MM-DD HH:mm:ss');
      const endDate = values.registerEndDate?.format('YYYY-MM-DD HH:mm:ss');
      if (startDate || endDate) {
        conditions.push({ type: 'register_time', logic: 'AND', config: { start_date: startDate || null, end_date: endDate || null } });
      }
    }

    if (enabledConditions.includes('level')) {
      const minLevel = values.minLevel;
      const maxLevel = values.maxLevel;
      if (hasValue(minLevel) || hasValue(maxLevel)) {
        conditions.push({ type: 'level', logic: 'AND', config: { min_level: hasValue(minLevel) ? minLevel : null, max_level: hasValue(maxLevel) ? maxLevel : null } });
      }
    }

    if (enabledConditions.includes('vip')) {
      const minVip = values.minVip;
      const maxVip = values.maxVip;
      if (hasValue(minVip) || hasValue(maxVip)) {
        conditions.push({ type: 'vip', logic: 'AND', config: { min_vip: hasValue(minVip) ? minVip : null, max_vip: hasValue(maxVip) ? maxVip : null } });
      }
    }

    if (enabledConditions.includes('today_recharge')) {
      const minAmount = values.minTodayRecharge;
      const maxAmount = values.maxTodayRecharge;
      if (hasValue(minAmount) || hasValue(maxAmount)) {
        conditions.push({ type: 'today_recharge', logic: 'AND', config: { min_amount: hasValue(minAmount) ? minAmount : null, max_amount: hasValue(maxAmount) ? maxAmount : null } });
      }
    }

    if (enabledConditions.includes('total_recharge')) {
      const minAmount = values.minTotalRecharge;
      const maxAmount = values.maxTotalRecharge;
      if (hasValue(minAmount) || hasValue(maxAmount)) {
        conditions.push({ type: 'total_recharge', logic: 'AND', config: { min_amount: hasValue(minAmount) ? minAmount : null, max_amount: hasValue(maxAmount) ? maxAmount : null } });
      }
    }

    return conditions.length > 0 ? conditions : null;
  };

  const handleCreate = async (values) => {
    try {
      const items = selectedItems.map(item => {
        if (item.type === 'aoz') {
          return { type: 'aoz', subtype: item.subtype, count: item.count };
        }
        return { gid: item.id, name: item.name, count: item.count, level: item.level ?? 0 };
      });

      const limitConditions = buildLimitConditions(values);

      const payload = {
        name: values.name,
        description: values.description,
        items,
        limit_conditions: limitConditions,
        total_limit: values.totalLimit || 0,
        per_user_limit: values.perUserLimit || 1,
        start_time: values.timeRange?.[0]?.format('YYYY-MM-DD HH:mm:ss') || null,
        end_time: values.timeRange?.[1]?.format('YYYY-MM-DD HH:mm:ss') || null,
        status: values.status ? 1 : 0
      };

      await giftAPI.create(payload);
      message.success('福利创建成功');
      setCreateVisible(false);
      createForm.resetFields();
      setSelectedItems([]);
      setEnabledConditions([]);
      loadGifts();
    } catch (error) {
      message.error('创建失败：' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = async (values) => {
    try {
      const items = selectedItems.map(item => {
        if (item.type === 'aoz') {
          return { type: 'aoz', subtype: item.subtype, count: item.count };
        }
        return { gid: item.id, name: item.name, count: item.count, level: item.level ?? 0 };
      });

      const limitConditions = buildLimitConditions(values);

      const payload = {
        name: values.name,
        description: values.description,
        items,
        limit_conditions: limitConditions,
        total_limit: values.totalLimit || 0,
        per_user_limit: values.perUserLimit || 1,
        start_time: values.timeRange?.[0]?.format('YYYY-MM-DD HH:mm:ss') || null,
        end_time: values.timeRange?.[1]?.format('YYYY-MM-DD HH:mm:ss') || null,
        status: values.status ? 1 : 0
      };

      await giftAPI.update(currentGift.id, payload);
      message.success('福利更新成功');
      setEditVisible(false);
      editForm.resetFields();
      setSelectedItems([]);
      setEnabledConditions([]);
      loadGifts();
    } catch (error) {
      message.error('更新失败：' + (error.response?.data?.message || error.message));
    }
  };

  const handleDelete = async (id) => {
    try {
      await giftAPI.delete(id);
      message.success('福利删除成功');
      loadGifts();
    } catch (error) {
      message.error('删除失败：' + (error.response?.data?.message || error.message));
    }
  };

  const handleViewDetail = (gift) => {
    setCurrentGift(gift);
    setDetailVisible(true);
  };

  const handleViewLogs = async (giftId) => {
    setLogsLoading(true);
    setCurrentGift({ id: giftId });
    setLogsVisible(true);
    try {
      const response = await giftAPI.getLogs({ package_id: giftId, page: 1, limit: 20 });
      setLogs(response.data.data.logs);
      setLogsPagination(prev => ({ ...prev, total: response.data.data.pagination.total }));
    } catch (error) {
      message.error('加载领取记录失败');
    } finally {
      setLogsLoading(false);
    }
  };

  const handleStatusChange = async (giftId, status) => {
    try {
      await giftAPI.update(giftId, { status: status ? 1 : 0 });
      message.success(status ? '福利已启用' : '福利已停用');
      loadGifts();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    {
      title: '福利名称',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (text) => <span style={{ fontWeight: 'bold' }}>{text}</span>
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status, record) => (
        <Switch
          checked={status === 1}
          onChange={(checked) => handleStatusChange(record.id, checked)}
          checkedChildren="启用"
          unCheckedChildren="停用"
        />
      )
    },
    {
      title: '领取次数',
      key: 'claim_count',
      width: 100,
      render: (_, record) => (
        <span>{record.claim_count} / {record.total_limit > 0 ? record.total_limit : '∞'}</span>
      )
    },
    {
      title: '限制条件',
      key: 'limit_conditions',
      width: 150,
      render: (_, record) => {
        const conditions = record.limit_conditions || [];
        if (conditions.length === 0) return <Tag>无限制</Tag>;
        const typeMap = {
          account: '账户',
          character: '角色',
          purchase_limit: '购买次数',
          daily_claim_limit: '每日领取次数',
          register_time: '注册时间',
          level: '等级',
          vip: 'VIP',
          today_recharge: '今日充值',
          total_recharge: '累计充值'
        };
        return (
          <Space wrap>
            {conditions.map((c, idx) => (
              <Tag key={idx} color="blue">{typeMap[c.type] || c.type}</Tag>
            ))}
          </Space>
        );
      }
    },
    {
      title: '有效期',
      key: 'time_range',
      width: 160,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontSize: 12 }}>{record.start_time ? new Date(record.start_time).toLocaleDateString() : '立即'}</span>
          <span style={{ fontSize: 12, color: '#999' }}>{record.end_time ? new Date(record.end_time).toLocaleDateString() : '永久'}</span>
        </Space>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space>
          <Tooltip title="查看详情">
            <Button size="small" icon={<EyeOutlined />} onClick={() => handleViewDetail(record)} />
          </Tooltip>
          <Tooltip title="编辑">
            <Button size="small" icon={<EditOutlined />} onClick={() => { setCurrentGift(record); openEditForm(record); setEditVisible(true); }} />
          </Tooltip>
          <Tooltip title="领取记录">
            <Button size="small" icon={<ClockCircleOutlined />} onClick={() => handleViewLogs(record.id)} />
          </Tooltip>
          <Popconfirm title="确定删除此福利吗？" onConfirm={() => handleDelete(record.id)} okText="确定" cancelText="取消">
            <Button size="small" danger icon={<DeleteOutlined />} disabled={record.claim_count > 0} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const logColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    { title: '用户ID', dataIndex: 'user_id', key: 'user_id', width: 100 },
    { title: '角色名', dataIndex: 'user_name', key: 'user_name', width: 120 },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => status === 1 ? <Tag color="success"><CheckCircleOutlined /> 成功</Tag> : <Tag color="error"><CloseCircleOutlined /> 失败</Tag>
    },
    { title: '失败原因', dataIndex: 'error_msg', key: 'error_msg', ellipsis: true },
    {
      title: '领取时间',
      dataIndex: 'claim_time',
      key: 'claim_time',
      width: 160,
      render: (text) => text ? new Date(text).toLocaleString() : '-'
    }
  ];

  const openEditForm = (record) => {
    const items = (record.items || []).map(item => {
      if (item.type === 'aoz') {
        const aozMap = { normal: 67174445, free: 67174446, gm: 67174447 };
        return {
          id: aozMap[item.subtype] || 67174445,
          name: item.subtype === 'free' ? '免费AOZ' : item.subtype === 'gm' ? 'GM AOZ' : 'AOZ',
          type: 'aoz',
          subtype: item.subtype,
          count: item.count,
          uniqueId: `${aozMap[item.subtype] || 67174445}_0`
        };
      }
      const gid = item.gid ?? item.item_id ?? item.id;
      const level = item.level ?? 0;
      return {
        id: gid,
        name: item.name || `道具${gid}`,
        type: 'item',
        level,
        count: item.count,
        uniqueId: `${gid}_${level}`
      };
    });
    setSelectedItems(items);

    const conditions = record.limit_conditions || [];
    const enabledTypes = conditions.map(c => c.type);
    setEnabledConditions(enabledTypes);

    const formValues = {};
    conditions.forEach(c => {
      if (c.type === 'account' && c.config?.account_ids) {
        formValues.accountIds = c.config.account_ids.join(', ');
      }
      if (c.type === 'character' && c.config?.character_ids) {
        formValues.characterIds = c.config.character_ids.join(', ');
      }
      if (c.type === 'purchase_limit' && hasValue(c.config?.max_purchase_count)) {
        formValues.maxPurchaseCount = c.config.max_purchase_count;
      }
      if (c.type === 'daily_claim_limit' && hasValue(c.config?.max_daily_claim_count)) {
        formValues.maxDailyClaimCount = c.config.max_daily_claim_count;
      }
      if (c.type === 'register_time') {
        if (c.config?.start_date) formValues.registerStartDate = dayjs(c.config.start_date);
        if (c.config?.end_date) formValues.registerEndDate = dayjs(c.config.end_date);
      }
      if (c.type === 'level') {
        if (hasValue(c.config?.min_level)) formValues.minLevel = c.config.min_level;
        if (hasValue(c.config?.max_level)) formValues.maxLevel = c.config.max_level;
      }
      if (c.type === 'vip') {
        if (hasValue(c.config?.min_vip)) formValues.minVip = c.config.min_vip;
        if (hasValue(c.config?.max_vip)) formValues.maxVip = c.config.max_vip;
      }
      if (c.type === 'today_recharge') {
        if (hasValue(c.config?.min_amount)) formValues.minTodayRecharge = c.config.min_amount;
        if (hasValue(c.config?.max_amount)) formValues.maxTodayRecharge = c.config.max_amount;
      }
      if (c.type === 'total_recharge') {
        if (hasValue(c.config?.min_amount)) formValues.minTotalRecharge = c.config.min_amount;
        if (hasValue(c.config?.max_amount)) formValues.maxTotalRecharge = c.config.max_amount;
      }
    });

    const timeRange = [];
    if (record.start_time) timeRange.push(dayjs(record.start_time));
    if (record.end_time) timeRange.push(dayjs(record.end_time));

    editForm.setFieldsValue({
      name: record.name,
      description: record.description,
      totalLimit: record.total_limit,
      perUserLimit: record.per_user_limit,
      timeRange: timeRange.length === 2 ? timeRange : null,
      status: record.status === 1,
      ...formValues
    });
  };

  const renderItems = (items) => {
    if (!items || !Array.isArray(items)) return '-';
    return items.map((item, idx) => {
      if (item.type === 'aoz') {
        const subtype = item.subtype === 'free' ? '免费AOZ' : item.subtype === 'gm' ? 'GM AOZ' : 'AOZ';
        return <Tag key={idx} color="purple">{subtype} x{item.count}</Tag>;
      }
      const gid = item.gid ?? item.item_id ?? item.id;
      const name = item.name || `道具${gid}`;
      const levelSuffix = item.level ? ` Lv.${item.level}` : '';
      return <Tag key={idx} color="blue">{name}{levelSuffix} x{item.count}</Tag>;
    });
  };

  const renderLimitConditionsText = (gift) => {
    const conditions = gift.limit_conditions || [];
    if (conditions.length === 0) return '无限制';
    const typeMap = {
      account: '指定账户',
      character: '指定角色',
      purchase_limit: '购买次数限制',
      daily_claim_limit: '每日领取次数限制',
      register_time: '注册时间限制',
      level: '等级限制',
      vip: 'VIP限制',
      today_recharge: '今日充值限制',
      total_recharge: '累计充值限制'
    };
    return conditions.map(c => typeMap[c.type] || c.type).join('、');
  };

  const conditionOptions = [
    { value: 'account', label: '玩家账户限制' },
    { value: 'character', label: '玩家角色限制' },
    { value: 'purchase_limit', label: '购买次数限制' },
    { value: 'daily_claim_limit', label: '每日领取次数限制' },
    { value: 'register_time', label: '注册时间限制' },
    { value: 'level', label: '玩家等级限制' },
    { value: 'vip', label: 'VIP等级限制' },
    { value: 'today_recharge', label: '今日充值限制' },
    { value: 'total_recharge', label: '累计充值限制' }
  ];

  const renderConditionFields = (form) => (
    <>
      <Form.Item label="启用限制条件（可多选）">
        <Checkbox.Group
          value={enabledConditions}
          onChange={setEnabledConditions}
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
        >
          {conditionOptions.map(opt => (
            <Checkbox key={opt.value} value={opt.value}>{opt.label}</Checkbox>
          ))}
        </Checkbox.Group>
      </Form.Item>

      <Collapse defaultActiveKey={[]} style={{ marginBottom: 16 }}>
        <Panel header="玩家账户限制" key="account" disabled={!enabledConditions.includes('account')}>
          <Form.Item name="accountIds" label="允许的账户ID（逗号分隔）">
            <Input placeholder="例如：1001, 1002, 1003" />
          </Form.Item>
        </Panel>

        <Panel header="玩家角色限制" key="character" disabled={!enabledConditions.includes('character')}>
          <Form.Item name="characterIds" label="允许的角色ID（逗号分隔）">
            <Input placeholder="例如：2001, 2002, 2003" />
          </Form.Item>
        </Panel>

        <Panel header="购买次数限制" key="purchase_limit" disabled={!enabledConditions.includes('purchase_limit')}>
          <Form.Item name="maxPurchaseCount" label="每人限购次数">
            <InputNumber min={1} placeholder="例如：1" style={{ width: '100%' }} />
          </Form.Item>
        </Panel>

        <Panel header="每日领取次数限制" key="daily_claim_limit" disabled={!enabledConditions.includes('daily_claim_limit')}>
          <Form.Item name="maxDailyClaimCount" label="每人每日可领取次数">
            <InputNumber min={1} placeholder="例如：1" style={{ width: '100%' }} />
          </Form.Item>
        </Panel>

        <Panel header="注册时间限制" key="register_time" disabled={!enabledConditions.includes('register_time')}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="registerStartDate" label="注册起始时间">
                <DatePicker showTime style={{ width: '100%' }} placeholder="不限制" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="registerEndDate" label="注册结束时间">
                <DatePicker showTime style={{ width: '100%' }} placeholder="不限制" />
              </Form.Item>
            </Col>
          </Row>
        </Panel>

        <Panel header="玩家等级限制" key="level" disabled={!enabledConditions.includes('level')}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="minLevel" label="最低等级">
                <InputNumber placeholder="不限制" style={{ width: '100%' }} min={1} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="maxLevel" label="最高等级">
                <InputNumber placeholder="不限制" style={{ width: '100%' }} min={1} />
              </Form.Item>
            </Col>
          </Row>
        </Panel>

        <Panel header="VIP等级限制" key="vip" disabled={!enabledConditions.includes('vip')}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="minVip" label="最低VIP">
                <InputNumber placeholder="不限制" style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="maxVip" label="最高VIP">
                <InputNumber placeholder="不限制" style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
          </Row>
        </Panel>

        <Panel header="今日充值限制" key="today_recharge" disabled={!enabledConditions.includes('today_recharge')}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="minTodayRecharge" label="今日最低充值">
                <InputNumber placeholder="不限制" style={{ width: '100%' }} min={0} precision={2} addonAfter="元" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="maxTodayRecharge" label="今日最高充值">
                <InputNumber placeholder="不限制" style={{ width: '100%' }} min={0} precision={2} addonAfter="元" />
              </Form.Item>
            </Col>
          </Row>
        </Panel>

        <Panel header="累计充值限制" key="total_recharge" disabled={!enabledConditions.includes('total_recharge')}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="minTotalRecharge" label="累计最低充值">
                <InputNumber placeholder="不限制" style={{ width: '100%' }} min={0} precision={2} addonAfter="元" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="maxTotalRecharge" label="累计最高充值">
                <InputNumber placeholder="不限制" style={{ width: '100%' }} min={0} precision={2} addonAfter="元" />
              </Form.Item>
            </Col>
          </Row>
        </Panel>
      </Collapse>
    </>
  );

  const giftFormItems = (form, isEdit) => (
    <>
      <Form.Item name="name" label="福利名称" rules={[{ required: true, message: '请输入福利名称' }]}>
        <Input placeholder="请输入福利名称" />
      </Form.Item>
      <Form.Item name="description" label="福利描述">
        <TextArea rows={2} placeholder="请输入福利描述" />
      </Form.Item>

      <Divider orientation="left">福利内容</Divider>
      <Form.Item label="福利内容">
        <Button type="primary" icon={<GiftOutlined />} onClick={handleOpenItemSelector}>
          选择道具
        </Button>
        {selectedItems.length > 0 && (
          <Tag color="green" style={{ marginLeft: 8 }}>已选 {selectedItems.length} 个道具</Tag>
        )}
      </Form.Item>

      {selectedItems.length > 0 && (
        <Card size="small" title="已选道具" style={{ marginBottom: 16 }}>
          <Row gutter={[8, 8]}>
            {selectedItems.map(item => (
              <Col span={8} key={item.uniqueId || item.id}>
                <Card size="small">
                  <Space style={{ justifyContent: 'space-between', width: '100%' }}>
                    <Text ellipsis style={{ maxWidth: 120 }}>{item.name}{item.level ? ` Lv.${item.level}` : ''}</Text>
                    <InputNumber size="small" min={1} value={item.count} onChange={(value) => handleUpdateItemCount(item.uniqueId || item.id, value)} style={{ width: 60 }} />
                    <Button size="small" danger type="text" onClick={() => handleRemoveItem(item.uniqueId || item.id)}>移除</Button>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>
        </Card>
      )}

      <Divider orientation="left">领取限制</Divider>
      {renderConditionFields(form)}

      <Divider orientation="left">其他设置</Divider>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="perUserLimit" label="每用户次数" initialValue={1}>
            <InputNumber placeholder="0=不限制" style={{ width: '100%' }} min={0} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="totalLimit" label="总领取次数" initialValue={0}>
            <InputNumber placeholder="0=不限制" style={{ width: '100%' }} min={0} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name="timeRange" label="有效期">
            <DatePicker.RangePicker showTime style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="status" label="状态" initialValue={true} valuePropName="checked">
        <Switch checkedChildren="启用" unCheckedChildren="停用" />
      </Form.Item>
    </>
  );

  return (
    <div>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic title="福利总数" value={pagination.total} prefix={<GiftOutlined />} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="启用中" value={gifts.filter(g => g.status === 1).length} prefix={<CheckCircleOutlined />} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic title="总领取次数" value={gifts.reduce((sum, g) => sum + (g.claim_count || 0), 0)} prefix={<UserOutlined />} valueStyle={{ color: '#faad14' }} />
          </Card>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={8}>
            <Input.Search placeholder="搜索福利名称" onSearch={setSearchKeyword} prefix={<SearchOutlined />} size="large" />
          </Col>
          <Col span={4}>
            <Button onClick={loadGifts} icon={<ReloadOutlined />} size="large">刷新</Button>
          </Col>
          <Col span={12} style={{ textAlign: 'right' }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { createForm.resetFields(); setSelectedItems([]); setEnabledConditions([]); setCreateVisible(true); }} size="large">
              创建福利
            </Button>
          </Col>
        </Row>
      </Card>

      <Table columns={columns} dataSource={gifts} rowKey="id" loading={loading} pagination={pagination} onChange={(pag) => setPagination(pag)} size="middle" />

      <Modal title="创建福利" open={createVisible} onCancel={() => setCreateVisible(false)} onOk={() => createForm.submit()} width={700}>
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          {giftFormItems(createForm, false)}
        </Form>
      </Modal>

      <Modal title="编辑福利" open={editVisible} onCancel={() => setEditVisible(false)} onOk={() => editForm.submit()} width={700}>
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          {giftFormItems(editForm, true)}
        </Form>
      </Modal>

      <Drawer title="福利详情" placement="right" open={detailVisible} onClose={() => setDetailVisible(false)} width={600}>
        {currentGift && (
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="福利名称">{currentGift.name}</Descriptions.Item>
            <Descriptions.Item label="描述">{currentGift.description || '-'}</Descriptions.Item>
            <Descriptions.Item label="福利内容">{renderItems(currentGift.items)}</Descriptions.Item>
            <Descriptions.Item label="限制条件">{renderLimitConditionsText(currentGift)}</Descriptions.Item>
            {currentGift.limit_conditions && currentGift.limit_conditions.map((c, idx) => {
              if (c.type === 'account' && c.config?.account_ids) {
                return <Descriptions.Item key={idx} label={`账户限制`}>{c.config.account_ids.join(', ')}</Descriptions.Item>;
              }
              if (c.type === 'character' && c.config?.character_ids) {
                return <Descriptions.Item key={idx} label={`角色限制`}>{c.config.character_ids.join(', ')}</Descriptions.Item>;
              }
              if (c.type === 'purchase_limit' && c.config?.max_purchase_count) {
                return <Descriptions.Item key={idx} label={`购买次数限制`}>{c.config.max_purchase_count} 次</Descriptions.Item>;
              }
              if (c.type === 'daily_claim_limit' && c.config?.max_daily_claim_count) {
                return <Descriptions.Item key={idx} label={`每日领取次数限制`}>{c.config.max_daily_claim_count} 次/日</Descriptions.Item>;
              }
              if (c.type === 'register_time') {
                return (
                  <Descriptions.Item key={idx} label={`注册时间限制`}>
                    {c.config?.start_date || '不限制'} 至 {c.config?.end_date || '不限制'}
                  </Descriptions.Item>
                );
              }
              if (c.type === 'level') {
                return (
                  <Descriptions.Item key={idx} label={`等级限制`}>
                    {c.config?.min_level || '不限制'} - {c.config?.max_level || '不限制'}
                  </Descriptions.Item>
                );
              }
              if (c.type === 'vip') {
                return (
                  <Descriptions.Item key={idx} label={`VIP限制`}>
                    {c.config?.min_vip || '不限制'} - {c.config?.max_vip || '不限制'}
                  </Descriptions.Item>
                );
              }
              if (c.type === 'today_recharge') {
                return (
                  <Descriptions.Item key={idx} label={`今日充值限制`}>
                    ¥{c.config?.min_amount || '0'} - ¥{c.config?.max_amount || '不限制'}
                  </Descriptions.Item>
                );
              }
              if (c.type === 'total_recharge') {
                return (
                  <Descriptions.Item key={idx} label={`累计充值限制`}>
                    ¥{c.config?.min_amount || '0'} - ¥{c.config?.max_amount || '不限制'}
                  </Descriptions.Item>
                );
              }
              return null;
            })}
            <Descriptions.Item label="总领取次数">{currentGift.total_limit > 0 ? currentGift.total_limit : '不限制'}</Descriptions.Item>
            <Descriptions.Item label="每用户次数">{currentGift.per_user_limit > 0 ? currentGift.per_user_limit : '不限制'}</Descriptions.Item>
            <Descriptions.Item label="已领取次数">{currentGift.claim_count}</Descriptions.Item>
            <Descriptions.Item label="开始时间">{currentGift.start_time ? new Date(currentGift.start_time).toLocaleString() : '立即'}</Descriptions.Item>
            <Descriptions.Item label="结束时间">{currentGift.end_time ? new Date(currentGift.end_time).toLocaleString() : '永久'}</Descriptions.Item>
            <Descriptions.Item label="状态">{currentGift.status === 1 ? '启用' : '停用'}</Descriptions.Item>
          </Descriptions>
        )}
      </Drawer>

      <Modal title="领取记录" open={logsVisible} onCancel={() => setLogsVisible(false)} footer={null} width={800}>
        <Table columns={logColumns} dataSource={logs} rowKey="id" loading={logsLoading} pagination={logsPagination} onChange={(pag) => setLogsPagination(pag)} size="small" />
      </Modal>

      <Modal
        title={null}
        open={itemModalVisible}
        onCancel={() => setItemModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setItemModalVisible(false)}>取消</Button>,
          <Button key="confirm" type="primary" onClick={handleConfirmItems}>确认选择 ({selectedItems.length}个道具)</Button>
        ]}
        width={1120}
        className="mail-item-modal"
      >
        <div className="mail-item-picker">
          <div className="mail-modal-hero mail-modal-hero-compact">
            <div>
              <div className="mail-modal-title">物品选择</div>
              <div className="mail-modal-subtitle">左侧浏览福利物品库，右侧实时查看已选福利内容并继续调整等级与数量。</div>
            </div>
            <div className="mail-modal-hero-tags">
              <Tag color="blue">可选 {
                itemList.filter((item) => {
                  const searchText = itemSearchText.toLowerCase().trim();
                  if (!searchText) return true;
                  return String(item.id).includes(searchText) || (item.name || '').toLowerCase().includes(searchText);
                }).length
              } 个</Tag>
              <Tag color="green">已选 {selectedItems.length} 个</Tag>
            </div>
          </div>

          <div className="mail-item-picker-toolbar">
            <Input
              placeholder="搜索道具：输入 ID 或名称"
              allowClear
              value={itemSearchText}
              onChange={(e) => setItemSearchText(e.target.value)}
              prefix={<SearchOutlined />}
              className="mail-item-search"
            />
          </div>

          <div className="mail-item-picker-body">
            <div className="mail-item-library">
              <div className="mail-item-grid">
                {itemList.filter((item) => {
                  const searchText = itemSearchText.toLowerCase().trim();
                  if (!searchText) return true;
                  return String(item.id).includes(searchText) || (item.name || '').toLowerCase().includes(searchText);
                }).map((record) => {
                  const isAdded = record.levels && record.levels.length > 0
                    ? record.levels.some((lvl) => selectedItems.some((si) => si.uniqueId === `${record.id}_${lvl.level}`))
                    : selectedItems.some((si) => si.uniqueId === `${record.id}_${record.defaultLevel || 1}`);
                  const colors = ['default', 'green', 'blue', 'purple', 'orange', 'red'];

                  return (
                    <div key={record.id} className={`mail-item-card${isAdded ? ' mail-item-card-added' : ''}`}>
                      <div className="mail-item-card-head">
                        <div>
                          <div className="mail-item-card-title">{record.name || '未知道具'}</div>
                          <div className="mail-item-card-subtitle">ID {record.id}</div>
                        </div>
                        <Tag color={colors[record.quality] || 'default'}>{record.quality || 0}星</Tag>
                      </div>
                      <div className="mail-item-card-meta">
                        <span>{record.levels && record.levels.length > 0 ? `${record.levels.length} 个等级` : '单等级道具'}</span>
                        <span>{record.type || 'item'}</span>
                      </div>
                      <div className="mail-item-card-desc">{record.description || '暂无描述'}</div>
                      <Button
                        type={isAdded ? 'default' : 'primary'}
                        disabled={isAdded}
                        onClick={() => handleSelectItem(record)}
                        block
                      >
                        {isAdded ? '已添加' : '加入福利'}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mail-item-sidebar">
              <div className="mail-form-section mail-form-section-tight">
                <div className="mail-form-section-head">
                  <div className="mail-form-section-title">已选福利内容</div>
                  <div className="mail-form-section-subtitle">在确认前可继续修改等级、数量或移除单项。</div>
                </div>
                {selectedItems.length > 0 ? (
                  <div className="mail-selected-list">
                    {selectedItems.map((item) => (
                      <div key={item.uniqueId || item.id} className="mail-selected-item">
                        <div className="mail-selected-item-main">
                          <div className="mail-selected-item-title">{item.name || `道具${item.id}`}{item.level ? ` Lv.${item.level}` : ''}</div>
                          <div className="mail-selected-item-meta">道具 ID {item.id}</div>
                        </div>
                        <div className="mail-selected-item-controls">
                          <InputNumber
                            min={1}
                            max={MAX_REWARD_COUNT}
                            value={item.count}
                            onChange={(value) => handleUpdateItemCount(item.uniqueId || item.id, value)}
                            addonBefore="数量"
                            size="small"
                            className="field-width-120"
                          />
                          <Button type="text" danger size="small" onClick={() => handleRemoveItem(item.uniqueId || item.id)}>
                            移除
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mail-empty-panel">
                    <div className="mail-empty-panel-title">右侧清单为空</div>
                    <div className="mail-empty-panel-text">从左侧物品库加入道具后，这里会实时显示确认结果。</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        title={`选择等级 - ${currentItemForLevel?.name || ''}`}
        open={levelModalVisible}
        onCancel={() => {
          setLevelModalVisible(false);
          setCurrentItemForLevel(null);
          setSelectedLevel(null);
        }}
        onOk={handleConfirmLevel}
        width={440}
        okText="确认选择"
        cancelText="取消"
      >
        <div style={{ maxHeight: '300px', overflow: 'auto' }}>
          <Table
            columns={[
              { title: '等级', dataIndex: 'level', key: 'level', width: 70, render: (level) => <Tag color="blue">Lv.{level}</Tag> },
              { title: '品质', dataIndex: 'quality', key: 'quality', width: 70, render: (quality) => <Tag>{quality || '-'}</Tag> },
              { title: '描述', dataIndex: 'description', key: 'description', width: 150, ellipsis: true, render: (text) => <Text type="secondary" ellipsis={{ tooltip: text || '-' }}>{text || '-'}</Text> },
              {
                title: '操作', key: 'action', width: 88,
                render: (_, record) => (
                  <Button size="small" type={selectedLevel === record.level ? 'primary' : 'default'} onClick={() => setSelectedLevel(record.level)}>
                    {selectedLevel === record.level ? '已选' : '选择'}
                  </Button>
                )
              }
            ]}
            dataSource={currentItemForLevel?.levels || []}
            rowKey="level"
            pagination={false}
            size="small"
            tableLayout="fixed"
            scroll={{ x: 378 }}
          />
        </div>
      </Modal>
    </div>
  );
}

export default GiftManagement;
